import axios from '@/services/request';
import {AxiosPromise} from "axios";
import store from "@/store";
import {SpuManage, TSku} from "@/model/Spu";
import {Resp, Page} from "@/model/Resp";
import ItemManage from "@/model/Item";
import {SpecManage, SpecSelect, specSpu} from "@/model/Spec";
import Task from "@/model/Task";

/**
 * item列表
 * @param params
 * @param showLoading
 */
export function getEditItemList(params: {
  country?: string,
  language?: string,
  itemId?: number,
  tempLinkUrl?: string,
  creater?: string,
  startDate?: string,
  endDate?: string,
  currency?: string,
  pageNo: number,
  pageSize: number
}, showLoading: boolean = false): AxiosPromise<Resp<Page<{
  status: number,
  itemId: number,
  orderCount: number,
  statusStr: string
}>>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/item/list/not_associated`, params)
}

/**
 * item查询
 */
export function queryItemDetail(params: { itemId: number, taskId: number|undefined }, showLoading: boolean = false): AxiosPromise<Resp<ItemManage>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/item/detail/query`, params)
}

/**
 * item保存
 */
export function saveItemDetail(params: ItemManage, showLoading: boolean = false): AxiosPromise<Resp<ItemManage>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/item/detail/save`, params)
}


/**
 * 对齐规格信息
 */
export function checkSpecAlign(params: ItemManage, showLoading: boolean = false): AxiosPromise<Resp<SpecManage>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spec/align/check`, params)
}

/**
 * 对齐用的标准spu下拉列表
 */
export function getSpecSelect(params: { baseSpuId: number }, showLoading: boolean = false): AxiosPromise<Resp<SpecSelect>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spec/select`, params)
}

/**
 * 预览对齐信息
 */
export function previewSpecAlign(params: SpecManage, showLoading: boolean = false): AxiosPromise<Resp<specSpu[]>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spec/sku/preview`, params)
}

/**
 * 保存对齐信息
 */
export function saveSpecAlign(params: SpecManage, showLoading: boolean = false): AxiosPromise<Resp<any>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spec/align/save`, params)
}

/**
 * item关联spu查询
 */
export function recommendSpu(params: { itemId: number }, showLoading: boolean = false): AxiosPromise<Resp<SpuManage[]>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/item/recommend/spus`, params)
}

/**
 * spu关键字查询
 */
export function querySpu(params: { condition: string, name: string }, showLoading: boolean = false): AxiosPromise<Resp<SpuManage[]>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/queryData`, params)
}

/**
 * 获取spu的excel
 */
export function getItemExcel(params: { itemId: string }, showLoading: boolean = false): AxiosPromise<Resp<File>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios({
    url: `/api/out/download/perfect/anomaly/items`,
    method: 'get',
    params,
    responseType: 'blob'
  })
}

/**
 * 保存spu的excel
 */
export function saveItemExcel(params: FormData, showLoading: boolean = true): AxiosPromise<Resp<string>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/out/upload/perfect/anomaly/items`, params)
}

/**
 * spu列表查询
 */
export function querySpuList(params: {
  name?: string,
  spuId?: number,
  status?: number,
  pageNo: number,
  pageSize: number
}, showLoading: boolean = false): AxiosPromise<Resp<Page<SpuManage>>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/queryPage`, params)
}

/**
 * spu查询
 */
export function genSpu(params: { spuId?: number, supperlierUrl?: string }, showLoading: boolean = false): AxiosPromise<Resp<SpuManage>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/genData`, params)
}

/**
 * spu查询，直接查询 1688 的 spu，不查我们数据库
 */
export function genSpuByUrl(params: { supperlierUrl?: string }, showLoading: boolean = false): AxiosPromise<Resp<SpuManage>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/genDataByUrl`, params)
}

/**
 * 通过covers查询相似spuId
 */
export function genSpuSimilar(params: string[], showLoading: boolean = false): AxiosPromise<Resp<number[]>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/similar/ids`, params)
}

/**
 * sku填充查询
 */
export function getSkuFillList(params: { supperlierUrl?: string, refresh?: boolean }, showLoading: boolean = false): AxiosPromise<Resp<TSku[]>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/sku/list`, params)
}

/**
 * spu保存
 */
export function saveSpu(params: SpuManage, showLoading: boolean = false): AxiosPromise<Resp<SpuManage>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/saveData`, params)
}

/**
 * 生成sku(入参跟spu保存一致)
 */
export function genSkuList(params: SpuManage, showLoading: boolean = false): AxiosPromise<Resp<SpuManage>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/spu/gen/skus`, params)
}

/**
 * 登录
 * @param params
 * @param showLoading
 */
export function login(params: {
  userName: string,   // 用户
  password: string    // 密码
}, showLoading: boolean = false): AxiosPromise {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/user/login`, params)
}

/**
 * 登出
 * @param params
 * @param showLoading
 */
export function logout(params: {}, showLoading: boolean = false): AxiosPromise {
  showLoading && store.commit('SET_LOADING', true);
  return axios.get(`/api/user/logout`, {params})
}

/**
 * 获取配置
 * @param params
 * @param showLoading
 */
export function getConfig(params: {
  type: 'region' | 'language' | 'toushou' | 'domain' | 'spu_attr'| 'task_status'
}, showLoading: boolean = false): AxiosPromise {
  showLoading && store.commit('SET_LOADING', true);
  return axios.get(`/api/config/datas`, {params})
}


/**
 * 获取异常订单的excel
 */
export function getAbnormalOrderExcel(showLoading: boolean = false): AxiosPromise<Resp<File>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios({
    url: `/api/abnorder/export/abnormal/order`,
    method: 'get',
    responseType: 'blob'
  })
}

/**
 * 获取解析订单的excel模板
 */
export function getAbnormalOrderExcelTemplate(showLoading: boolean = false): AxiosPromise<Resp<File>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios({
    url: `/api/abnorder/export/abnormal/order/template`,
    method: 'get',
    responseType: 'blob'
  })
}

/**
 * 保存解析订单的excel
 */
export function saveAbnormalOrderExcel(params: FormData, showLoading: boolean = true): AxiosPromise<Resp<string>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/abnorder/upload/abnormal/order`, params)
}


/**
 * 申请任务
 */
export function applyTask(params: {}, showLoading: boolean = true): AxiosPromise<Resp<any>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.get(`/api/task/apply`, params)
}

/**
 * 任务列表
 */
export function getTaskList(params:  {
  taskKey?: string,
  userId?: number,
  startDate?: string,
  endDate?: string,
  status?: number,
  pageNo: number,
  pageSize: number
}, showLoading: boolean = true): AxiosPromise<Resp<Page<Task>>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/task/mylist`, params)
}

/**
 * 暂存任务
 */
export function suspendTask(params: {id: number}, showLoading: boolean = true): AxiosPromise<Resp<any>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/task/suspend`, params)
}

/**
 * 退回任务
 */
export function sendBackTask(params: {id: number}, showLoading: boolean = true): AxiosPromise<Resp<any>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/task/sendback`, params)
}

/**
 * 操作日志
 */
export function getTaskLogList(params: {taskKey: number, taskType: number}, showLoading: boolean = false): AxiosPromise<Resp<Page<Task>>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.post(`/api/task/loglist`, params)
}

/**
 * 操作日志
 */
export function getTaskTodayCount(showLoading: boolean = false): AxiosPromise<Resp<{datas: {oldProduct: number, newProduct: number}[]}>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios.get(`/api/task/todaycount`)
}

/**
 * 获取task的excel
 */
export function getTaskExcel(params: {
  userId?: number,
  startDate?: string,
  endDate?: string,
  status?: number,
  pageNo: number,
  pageSize: number
}, showLoading: boolean = false): AxiosPromise<Resp<File>> {
  showLoading && store.commit('SET_LOADING', true);
  return axios({
    url: `/api/task/download`,
    method: 'post',
    data: params,
    responseType: 'blob'
  })
}



